import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { identifierTypeTranslationMap } from 'src/features/eventLog/utils/constants';
import { EVENT_TYPE, MAIN_IDENTIFIER_TYPE } from 'src/features/eventLog/types';
import { useCreateSystemSetting } from 'src/features/systems/api/systemSettings/useCreateSystemSetting';
import {
    LIST_SYSTEM_SETTING_RESOURCE,
    useListSystemSetting,
} from 'src/features/systems/api/systemSettings/useListSystemSetting';
import { useUpdateSystemSetting } from 'src/features/systems/api/systemSettings/useUpdateSystemSetting';
import { eventTypeTranslationMap, membershipCategoryTranslationMap } from 'src/features/systems/utils/constants';
import { formSchema, SystemSettingFormSchemaValues } from 'src/features/systems/utils/formSchema';
import { MEMBERSHIP_CATEGORY, SYSTEM_SETTINGS_CATEGORY, SYSTEM_SETTINGS_CODE } from 'src/features/systems/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { Button } from 'src/registry/ui/Button';
import { Checkbox } from 'src/registry/ui/Checkbox';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';

const SystemSettings = () => {
    const { formatMessage } = useIntl();
    const toastShown = React.useRef(false);
    const { parkingId } = useParking();

    const { data: systemSettingsData } = useListSystemSetting({ parkingId });

    const { mutate: createSystemSetting, isPending } = useCreateSystemSetting({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SYSTEM_SETTING_RESOURCE] });
            form.reset();
            if (!toastShown.current) {
                toastShown.current = true;
                toast({
                    title: formatMessage(systemsMessages.systemSettingsCreated),
                    description: formatMessage(systemsMessages.systemSettingsCreatedSuccess),
                });
            }
        },
    });

    const { mutate: updateSystemSetting } = useUpdateSystemSetting({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SYSTEM_SETTING_RESOURCE] });
            form.reset();
            if (!toastShown.current) {
                toastShown.current = true;
                toast({
                    title: formatMessage(systemsMessages.systemSettingsUpdated),
                    description: formatMessage(systemsMessages.systemSettingsUpdatedSuccess),
                });
            }
        },
    });

    const systemSettingsDataResults = systemSettingsData?.results ?? [];

    const getDataBasedOnSettingCode = (code: SYSTEM_SETTINGS_CODE) => {
        return systemSettingsDataResults?.find((identifier) => identifier.code === code);
    };

    const supportedIdentifierTypeData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.SUPPORTED_IDENTIFIER_TYPES);
    const mainIdentifierTypeData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.MAIN_IDENTIFIER_TYPE);
    const enableMembershipCategoryData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.ENABLED_MEMBERSHIP_CATEGORIES);
    const enableEventTypeData = getDataBasedOnSettingCode(SYSTEM_SETTINGS_CODE.ENABLED_EVENT_TYPES);

    const defaultValues = {
        supported_identifier_type: (supportedIdentifierTypeData?.value as MAIN_IDENTIFIER_TYPE[]) ?? [],
        main_identifier_type: (mainIdentifierTypeData?.value as MAIN_IDENTIFIER_TYPE) ?? undefined,
        enabled_membership_categories: (enableMembershipCategoryData?.value as MEMBERSHIP_CATEGORY[]) ?? [],
        enabled_event_types: (enableEventTypeData?.value as EVENT_TYPE[]) ?? [],
    };

    const form = useForm<SystemSettingFormSchemaValues>({
        resolver: zodResolver(formSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, supportedIdentifierTypeData, mainIdentifierTypeData, enableMembershipCategoryData, enableEventTypeData]);

    const onSubmit: SubmitHandler<SystemSettingFormSchemaValues> = (values) => {
        toastShown.current = false;
        const dirtyFields = form.formState.dirtyFields;

        const editedFields = Object.keys(dirtyFields).reduce(
            (acc, key) => {
                const typedKey = key as keyof SystemSettingFormSchemaValues;
                acc[typedKey] = values[typedKey];
                return acc;
            },
            {} as Record<keyof SystemSettingFormSchemaValues, any>,
        );

        const supportedIdentifierTypeObject = {
            category: SYSTEM_SETTINGS_CATEGORY.VISITORS,
            code: SYSTEM_SETTINGS_CODE.SUPPORTED_IDENTIFIER_TYPES,
            value: values['supported_identifier_type'],
        };

        const mainIdentifierTypeObject = {
            category: SYSTEM_SETTINGS_CATEGORY.VISITORS,
            code: SYSTEM_SETTINGS_CODE.MAIN_IDENTIFIER_TYPE,
            value: values['main_identifier_type'],
        };

        const enableMembershipCategoryObject = {
            category: SYSTEM_SETTINGS_CATEGORY.VISITORS,
            code: SYSTEM_SETTINGS_CODE.ENABLED_MEMBERSHIP_CATEGORIES,
            value: values['enabled_membership_categories'],
        };

        const enabledEventTypesObject = {
            category: SYSTEM_SETTINGS_CATEGORY.SYSTEM,
            code: SYSTEM_SETTINGS_CODE.ENABLED_EVENT_TYPES,
            value: values['enabled_event_types'],
        };

        const settingsCodeFieldsMap = {
            supported_identifier_type: {
                settingData: supportedIdentifierTypeData,
                submitData: supportedIdentifierTypeObject,
            },
            main_identifier_type: { settingData: mainIdentifierTypeData, submitData: mainIdentifierTypeObject },
            enabled_membership_categories: {
                settingData: enableMembershipCategoryData,
                submitData: enableMembershipCategoryObject,
            },
            enabled_event_types: { settingData: enableEventTypeData, submitData: enabledEventTypesObject },
        };

        (
            Object.entries(settingsCodeFieldsMap) as [
                keyof typeof settingsCodeFieldsMap,
                { settingData: any; submitData: any },
            ][]
        ).forEach(([fieldName, { settingData, submitData }]) => {
            if (editedFields[fieldName]) {
                if (!settingData?.id) {
                    createSystemSetting({ ...submitData, parkingId });
                }
                updateSystemSetting({
                    id: settingData?.id ?? '',
                    parkingId,
                    ...submitData,
                });
            }
        });
    };

    const MultiSelectFormFieldBlock = ({
        title,
        fieldName,
        translationMap,
    }: {
        title: string;
        fieldName: 'supported_identifier_type' | 'enabled_membership_categories';
        translationMap:
            | Record<MAIN_IDENTIFIER_TYPE, { id: string; defaultMessage: string }>
            | Record<MEMBERSHIP_CATEGORY, { id: string; defaultMessage: string }>;
    }) => {
        return (
            <FormFieldBlock>
                <FormFieldBlockLabel className="md:w-1/3 lg:w-1/3">
                    <FormFieldBlockTitle>
                        <FormFieldBlockName className="flex flex-row gap-1">
                            {title}
                            <div className="text-primary-700">*</div>
                        </FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-2/3">
                    <FormField
                        control={form.control}
                        name={fieldName}
                        render={({ field }) => {
                            const { ref: _, ...rest } = field;
                            const fieldData = (field.value as [])
                                ?.map((membership) => {
                                    const [, value] = Object.entries(translationMap).find(
                                        ([key]) => key === membership,
                                    )!;

                                    return value ? formatMessage(value) : null;
                                })
                                .join(', ');

                            const allValues: string[] = Object.entries(translationMap).map(([key]) => key);

                            return (
                                <FormItem className="w-full">
                                    <FormControl>
                                        <MultiSelect
                                            {...rest}
                                            value={Array.isArray(field.value) ? field.value.map(String) : []}
                                        >
                                            <MultiSelectTrigger asChild>
                                                <MultiSelectControl className="w-full">
                                                    {fieldData?.length > 0 ? (
                                                        fieldData
                                                    ) : (
                                                        <span className="text-gray-400">{`${title}...`}</span>
                                                    )}
                                                </MultiSelectControl>
                                            </MultiSelectTrigger>
                                            <MultiSelectContent allValues={allValues}>
                                                {Object.entries(translationMap).map(([key, value]) => {
                                                    return (
                                                        <MultiSelectOption key={key} selected={key}>
                                                            {formatMessage(value)}
                                                        </MultiSelectOption>
                                                    );
                                                })}
                                            </MultiSelectContent>
                                        </MultiSelect>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            );
                        }}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
        );
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <MultiSelectFormFieldBlock
                title={formatMessage(systemsMessages.enabledIdentifierTypes)}
                fieldName="supported_identifier_type"
                translationMap={identifierTypeTranslationMap}
            />
            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel className="md:w-1/3 lg:w-1/3">
                    <FormFieldBlockTitle>
                        <FormFieldBlockName className="flex flex-row gap-1">
                            {formatMessage(systemsMessages.mainIdentifierType)}
                            <div className="text-primary-700">*</div>
                        </FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="w-2/3">
                    <FormField
                        control={form.control}
                        name="main_identifier_type"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={`${formatMessage(systemsMessages.mainIdentifierType)}...`}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(identifierTypeTranslationMap).map(([key, value]) => {
                                                return (
                                                    <SelectItem key={key} value={key}>
                                                        {formatMessage(value)}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
            <FormFieldBlockSeparator />

            <MultiSelectFormFieldBlock
                title={formatMessage(systemsMessages.enabledMembershipCategories)}
                fieldName="enabled_membership_categories"
                translationMap={membershipCategoryTranslationMap}
            />
            <FormFieldBlockSeparator />

            <FormFieldBlock className="grid grid-cols-1 gap-4">
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName className="flex flex-row gap-1">
                            {formatMessage(systemsMessages.enabledEventTypes)}
                            <div className="text-primary-700">*</div>
                        </FormFieldBlockName>
                    </FormFieldBlockTitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody className="md:w-full">
                    <FormField
                        control={form.control}
                        name="enabled_event_types"
                        render={({ field }) => (
                            <div className="grid grid-cols-1 gap-8 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                                {Object.entries(eventTypeTranslationMap).map(([key, label]) => {
                                    const event = key as EVENT_TYPE;
                                    return (
                                        <div className="flex flex-row items-center justify-start" key={key}>
                                            <Checkbox
                                                checked={field.value?.includes(event)}
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        field.onChange([...field.value, event]);
                                                    } else {
                                                        field.onChange(field.value.filter((v) => v !== event));
                                                    }
                                                }}
                                            />
                                            <span className="ml-2 whitespace-nowrap">{formatMessage(label)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>
            <FormFieldBlockSeparator />

            <div className="flex justify-end">
                <Button
                    className="w-full sm:w-auto"
                    type="submit"
                    onClick={form.handleSubmit(onSubmit)}
                    disabled={!form.formState.isDirty}
                >
                    {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="animate-spin text-lg" />}
                    {formatMessage(commonMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default SystemSettings;
